import { Button, Drawer, Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import MediaQuery from 'react-responsive'
import { useLocation } from 'react-router-dom'

import { useTitle } from 'hooks'
import { syncSelectedFacets } from 'store/actions/facets'
import { setFilterByKey } from 'store/actions/filters'
import { toggleCardView } from 'store/actions/pageSettings'
import { getPipelines } from 'store/actions/pipeline'
import { resetSearch, searchFundersByParams, syncURL } from 'store/actions/search'
import { CardViewEnum } from 'store/reducers/pageSettings'
import { selectAreFacetsLoaded } from 'store/selectors/facets'
import { selectAllFilters, selectIsResetButtonDisabled } from 'store/selectors/filters'
import { selectCardView } from 'store/selectors/pageSettings'
import { selectResultCount } from 'store/selectors/search'
import { selectPaginationState } from 'store/selectors/searchPagination'
import { selectHasExecutePermissions } from 'store/selectors/user'
import { useHotjar } from 'utils/hotjar'
import { trackPageView } from 'utils/mixpanel'

import CardViewChanger from 'components/CardViewChanger'
import Facets from 'components/Facets'
import Filters from 'components/Filters'
import PageInfo from 'components/Pagination/components/PageInfo'
import SearchBanner from 'components/SearchBanner'
import SearchHeader from 'components/SearchHeader'
import Pagination from 'components/SearchPagination'
import SearchResults from 'components/SearchResults'

const Search = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const { language, openToRequests, sortBy } = useSelector(selectAllFilters)
  const isResetButtonDisabled = useSelector(selectIsResetButtonDisabled)

  const [state, setState] = useState({
    showMobileDrawer: false
  })

  const { text: searchText, results: searchResults, loading } = useSelector(({ search }) => search)
  const cardView = useSelector(selectCardView)
  const areFacetsLoaded = useSelector(selectAreFacetsLoaded)
  const hasExecutePermissions = useSelector(selectHasExecutePermissions)

  const { viewsPerPage, pageNumber } = useSelector(selectPaginationState)
  const resultCount = useSelector(selectResultCount)

  useTitle(t => t.search.mainTitle)

  useHotjar()

  useEffect(() => {
    trackPageView()
  }, [])

  useEffect(() => {
    dispatch(syncURL(location.search))
  }, [])

  useEffect(() => {
    if (hasExecutePermissions) {
      dispatch(getPipelines())
    }
  }, [hasExecutePermissions])

  useEffect(() => {
    if (areFacetsLoaded) {
      dispatch(syncSelectedFacets)
      dispatch(searchFundersByParams())
    }
  }, [areFacetsLoaded])

  const handleReset = () => {
    dispatch(resetSearch())
    dispatch(searchFundersByParams())
  }

  return (
    <>
      <MediaQuery query="(max-width: 767px)">
        <Drawer
          open={state.showMobileDrawer}
          ModalProps={{ className: 'App Search-page__filters-mobile' }}
          onClose={() => setState({ showMobileDrawer: false })}
        >
          <Facets />

          <Filters
            language={language}
            openToRequests={openToRequests}
            sortBy={sortBy}
            onChange={({ key, value }) => dispatch(setFilterByKey({ [key]: value }))}
            showOnlySortBy={false}
            hideSortBy={true}
          />
        </Drawer>
      </MediaQuery>

      <Grid container className="main">
        <Grid size={{ xs: 12 }}>
          <SearchBanner />
        </Grid>

        <MediaQuery query="(min-width: 768px)">
          <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
            <Facets />
          </Grid>
        </MediaQuery>

        <Grid size={{ xs: 12, sm: 8, lg: 9 }} className="search-container">
          <SearchHeader
            onReset={handleReset}
            disableReset={isResetButtonDisabled}
            searchText={searchText}
          />

          <MediaQuery query="(min-width: 768px)">
            <Filters
              language={language}
              openToRequests={openToRequests}
              sortBy={sortBy}
              onChange={({ key, value }) => dispatch(setFilterByKey({ [key]: value }))}
              showOnlySortBy={false}
              hideSortBy={false}
            />
          </MediaQuery>

          <div>
            <Grid container>
              <Grid size={{ xs: 12, md: 10 }}>
                <div className="tw-flex tw-justify-between tw-items-center">
                  {!!resultCount && (
                    <PageInfo
                      className={'Search-page__results tw-flex-none'}
                      resultCount={resultCount}
                      viewsPerPage={viewsPerPage}
                      pageNumber={pageNumber}
                    />
                  )}
                  <div className="tw-block md:tw-hidden tw-flex tw-w-64 tw-justify-end">
                    <Filters
                      language={language}
                      openToRequests={openToRequests}
                      sortBy={sortBy}
                      onChange={({ key, value }) => dispatch(setFilterByKey({ [key]: value }))}
                      showOnlySortBy={true}
                      hideSortBy={false}
                    />
                    <div className="tw-mx-2">
                      <Button
                        className="Search-page__mobile-filters-button tw-ml-4"
                        onClick={() => setState({ showMobileDrawer: true })}
                        variant="text"
                      >
                        {t.search.filterMobile}
                      </Button>
                    </div>
                  </div>
                </div>
              </Grid>

              <MediaQuery query="(min-width: 769px)">
                <Grid size={{ md: 2 }}>
                  <CardViewChanger
                    onClick={() => dispatch(toggleCardView())}
                    expandedActive={cardView === CardViewEnum.EXPANDED ? 'active' : ''}
                    collapsedActive={cardView === CardViewEnum.COLLAPSED ? 'active' : ''}
                  />
                </Grid>
              </MediaQuery>
            </Grid>
          </div>

          <Grid container className="Search-page__results-container">
            <SearchResults loading={loading} results={searchResults} />
            {searchResults.length ? (
              <Grid container>
                <div className="tw-w-full">
                  <MediaQuery query="(min-width: 990px)">
                    <Pagination pagerSize={5} />
                  </MediaQuery>
                  <MediaQuery query="(max-width: 991px)">
                    <Pagination pagerSize={3} />
                  </MediaQuery>
                </div>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default Search
